import React, { FC, useEffect, useRef, useState } from 'react';
import { Checkbox, DatePickerProps, notification } from 'antd';
import { FrequencyDates, WeekDays, WeekDaysToFront } from 'shared/enums/news';
import moment from 'moment';
import { PICKER_DATE_MASK, PICKER_TIME_MASK, SERVER_DATE_MASK } from 'shared/constants';
import {
  checkDisabledDatesWithMinuteStep,
  disableAllPastDays,
  disabledStartTimePoll,
  getClosestBiggerTime,
  setDateFromString,
} from 'shared/helpers/date';
import { ALL_WEEK_DAYS } from 'shared/constants/news';
import dayjs from 'dayjs';
import DatePickerWithDots from 'shared/components/datePickerWithDots/DatePickerWithDots';
import TimePickerWithDots from 'shared/components/timePickerWithDots/TimePickerWithDots';
import { MINUTE_STEP } from 'shared/constants/date';
import { Calendar, DateObject } from 'shared/components/calendar/src';
import DatePanel from 'shared/components/calendar/src/plugins/date_panel/date_panel';
import style from './index.module.scss';

const checkboxOptions = [
  WeekDays.MONDAY,
  WeekDays.TUESDAY,
  WeekDays.WEDNESDAY,
  WeekDays.THURSDAY,
  WeekDays.FRIDAY,
  WeekDays.SUTURDAY,
  WeekDays.SUNDAY,
  WeekDays.EVERY_DAY_CHECKBOX,
];

type TProps = {
  datesToCreate: string[];
  startDate?: string;
  time: string;
  days: WeekDays[];
  retry: FrequencyDates;
  onDateChange: (dates: string[] | string, isStartDate: boolean) => void | undefined;
  onTimeChange: (dates: string | null) => void;
  onDaysChange: (dates: WeekDays[]) => void;
};

function checkWeekDays(weekDays: string[]): WeekDays[] {
  const days = weekDays.map(day => {
    const weekDay = WeekDaysToFront[day];

    return weekDay ? weekDay : day;
  });
  return days as WeekDays[];
}

const DateTime: FC<TProps> = ({ onDateChange, onDaysChange, onTimeChange, startDate: sd, ...props }) => {
  const [datesToCreate, setDatesToCreate] = useState<Date[]>([]);
  const [startDate, setStartDate] = useState<Date>();
  const [time, setTime] = useState<string>(props.time);
  const [days, setDays] = useState<WeekDays[]>(props.days);
  const [retry, setRetry] = useState<FrequencyDates>(props.retry);
  const isAllDaysSet = useRef<boolean>(false);

  const firstRender = useRef<boolean>(false);
  useEffect(() => {
    setTime(props.time);
  }, [props.time]);

  useEffect(() => {
    setDays(props.days);
  }, [props.days]);

  useEffect(() => {
    setRetry(props.retry);
    if (retry && firstRender.current) {
      onDaysChange([]);
      onDateChange([], false);
      onTimeChange(getClosestBiggerTime());
    }
    firstRender.current = true;
  }, [props.retry]);

  useEffect(() => {
    return () => {
      onDaysChange([]);
      onDateChange([], false);
      onTimeChange(getClosestBiggerTime());
    };
  }, []);

  useEffect(() => {
    setDatesToCreate(
      props.datesToCreate?.map(date => {
        const timestamp = Date.parse(date);
        let newDate: Date;
        if (isNaN(timestamp) == false) {
          newDate = new Date(timestamp);
        } else {
          newDate = moment(date, PICKER_DATE_MASK).toDate();
        }
        return newDate;
      }),
    );
  }, [props.datesToCreate]);

  useEffect(() => {
    if (sd) {
      const timestamp = Date.parse(sd);
      let newDate: Date;
      if (isNaN(timestamp) == false) {
        newDate = new Date(timestamp);
      } else {
        newDate = moment(sd, PICKER_DATE_MASK).toDate();
      }
      setStartDate(newDate);
    } else {
      setStartDate(undefined);
    }
  }, [sd]);

  const onDeferredDate: DatePickerProps['onChange'] = date => {
    onDateChange(date ? date.format(SERVER_DATE_MASK) : '', true);
  };

  const onDeferredTime: DatePickerProps['onChange'] = (date, dateString: string | string[]) => {
    if (Array.isArray(dateString)) {
      notification.error({ message: 'Время не может быть массивом' });
      return;
    }
    onTimeChange(dateString || null);
  };

  const onWeekDaysChanged = (weekdays: any) => {
    if (weekdays.includes(WeekDays.EVERY_DAY_CHECKBOX)) {
      isAllDaysSet.current = true;
      onDaysChange(ALL_WEEK_DAYS);
    } else {
      if (isAllDaysSet.current === true) {
        isAllDaysSet.current = false;
        onDaysChange([]);
      } else {
        onDaysChange(weekdays as WeekDays[]);
      }
    }
  };

  const onPickedDateChanged = (data: DateObject.DateObject[]) => {
    const prettyDate = data.map(date => date.format(SERVER_DATE_MASK));
    onDateChange(prettyDate, false);
  };

  if (retry === FrequencyDates.ONCE) {
    return (
      <div className={style.deferred}>
        <div className={style['deferred-date']}>
          {retry === FrequencyDates.ONCE ? <p>Дата</p> : <p>Дата начала</p>}
          <DatePickerWithDots
            format={PICKER_DATE_MASK}
            value={startDate ? dayjs(startDate) : undefined}
            onChange={onDeferredDate}
            disabledDate={current => checkDisabledDatesWithMinuteStep(current, true)}
          />
        </div>
        <div className={style['deferred-time']}>
          <p>Время</p>
          <TimePickerWithDots
            format={PICKER_TIME_MASK}
            minuteStep={MINUTE_STEP}
            value={setDateFromString(time, PICKER_TIME_MASK)}
            onChange={onDeferredTime}
            needConfirm={false}
            changeOnScroll={false}
            disabledTime={() => disabledStartTimePoll(undefined)}
          />
        </div>
      </div>
    );
  } else if (retry === FrequencyDates.WEEKLY) {
    return (
      <div className={style.deferred}>
        <div className={style['deferred-date']}>
          <p>Дата начала</p>
          <DatePickerWithDots
            format={PICKER_DATE_MASK}
            value={startDate ? dayjs(startDate) : undefined}
            onChange={onDeferredDate}
            disabledDate={current => checkDisabledDatesWithMinuteStep(current, true)}
          />
        </div>
        <div className={style['deferred-time']}>
          <p>Время</p>
          <TimePickerWithDots
            format={PICKER_TIME_MASK}
            minuteStep={MINUTE_STEP}
            value={setDateFromString(time, PICKER_TIME_MASK)}
            onChange={onDeferredTime}
            needConfirm={false}
            changeOnScroll={false}
            disabledTime={() => disabledStartTimePoll(undefined)}
          />
        </div>
        <div className={style['deferred-days']}>
          <p>Повторять</p>
          <Checkbox.Group value={checkWeekDays(days || [])} options={checkboxOptions} onChange={onWeekDaysChanged} />
        </div>
      </div>
    );
  } else {
    return (
      <div className={style.deferred}>
        <div className={style['deferred-time']}>
          <p>Время</p>
          <TimePickerWithDots
            format={PICKER_TIME_MASK}
            minuteStep={MINUTE_STEP}
            value={setDateFromString(time, PICKER_TIME_MASK)}
            onChange={onDeferredTime}
            needConfirm={false}
            changeOnScroll={false}
            disabledTime={() => disabledStartTimePoll(undefined)}
          />
        </div>
        <div className={style['deferred-dates']}>
          <p>Выберите даты</p>
          <Calendar
            value={datesToCreate?.map(date => moment(date).toDate()) || []}
            plugins={
              [
                <DatePanel
                  sort="date"
                  state={undefined}
                  setState={undefined}
                  position={undefined}
                  nodes={undefined}
                  handleChange={undefined}
                  eachDaysInRange={undefined}
                  onClickDate={undefined}
                  header={undefined}
                  markFocused={undefined}
                  handleFocusedDate={undefined}
                  formatFunction={undefined}
                />,
              ] as unknown as []
            }
            multiple
            highlightToday={false}
            onChange={onPickedDateChanged}
            mapDays={disableAllPastDays}
            calendar={undefined}
            locale={undefined}
            format={undefined}
            onlyMonthPicker={undefined}
            onlyYearPicker={undefined}
            className={undefined}
            role={undefined}
            weekDays={undefined}
            months={undefined}
            children={undefined}
            showOtherDays={undefined}
            minDate={undefined}
            maxDate={undefined}
            disableMonthPicker={undefined}
            disableYearPicker={undefined}
            formattingIgnoreList={undefined}
            onReady={undefined}
            sort={undefined}
            currentDate={undefined}
            digits={undefined}
            renderButton={undefined}
            disableDayPicker={undefined}
            onPropsChange={undefined}
            onMonthChange={undefined}
            onYearChange={undefined}
            onFocusedDateChange={undefined}
            readOnly={undefined}
            disabled={undefined}
            hideMonth={undefined}
            hideYear={undefined}
            hideWeekDays={undefined}
            fullYear={undefined}
            displayWeekNumbers={undefined}
            weekNumber={undefined}
            weekPicker={undefined}
            rangeHover={undefined}
            monthYearSeparator={undefined}
            formatMonth={undefined}
            formatYear={undefined}
            DatePicker={undefined}
            datePickerProps={undefined}
          />
        </div>
      </div>
    );
  }
};

export default DateTime;
